/* eslint-disable no-unused-vars */
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Box,
  alpha,
  Alert,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import { useEffect, useState } from 'react';
import tenant from '@/services/TenantService';
import ToastMessage from '@/components/toasts/ToastMessage';
import { toast } from 'react-toastify';

const TokenCopy = ({
  deployment,
  token,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) => {
  const [deploymentToken, setDeploymentToken] = useState(token);

  const [loading, setLoading] = useState(!token);
  const deploymentId = deployment?.insertedId || deployment?._id;

  useEffect(() => {
    const fetchToken = async () => {
      if (!token) {
        try {
          const response = await tenant.get('/token');
          const tokens = response.data;
          const matchingToken = tokens.find(
            (t) => t?.deploymentId === deploymentId,
          );
          setDeploymentToken(matchingToken?.token);
        } catch (error) {
          toast.error(
            <ToastMessage
              title="Fehler"
              subtitle="Token konnte nicht abgerufen werden"
            />,
          );
        } finally {
          setLoading(false);
        }
      }
    };

    fetchToken();
  }, [deployment?.insertedId, token]);

  const handleSave = () => {
    next();
  };

  if (loading) {
    return (
      <Card elevation={2} sx={{ borderRadius: 2, overflow: 'visible' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<InfoIcon color="primary" />}
        title={<Typography variant="h6">PMA Konfiguration</Typography>}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Alert severity="info" sx={{ mb: 4 }}>
          Bitte konfigurieren Sie Ihr PMA-Backend mit den folgenden Werten.
          Diese Werte sind notwendig für die Kommunikation mit unserem System.
        </Alert>

        <Stack spacing={4}>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Deployment ID
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Diese ID identifiziert Ihr Deployment in unserem System
            </Typography>
            <TextField
              fullWidth
              value={deploymentId}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(deploymentId);
                      toast.info(
                        <ToastMessage
                          title="Deployment ID kopiert"
                          subtitle="Die Deployment ID wurde in die Zwischenablage kopiert"
                        />,
                      );
                    }}
                    edge="end"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Dieser Token wird für die Authentifizierung benötigt
            </Typography>
            <TextField
              fullWidth
              value={deploymentToken}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(deploymentToken);
                      toast.info(
                        <ToastMessage
                          title="Token kopiert"
                          subtitle="Der Token wurde in die Zwischenablage kopiert"
                        />,
                      );
                    }}
                    edge="end"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            Bewahren Sie diese Werte sicher auf. Der Token ist
            sicherheitsrelevant und sollte vertraulich behandelt werden.
          </Alert>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      </CardActions>
    </Card>
  );
};

export default TokenCopy;
